/**
 * Canonical addon families — every legacy spelling of an add-on code maps to
 * one family, so matching is consistent across every page and server route.
 *
 * READ-side matching ONLY. Never use this module to rewrite stored codes,
 * prices, or Stripe metadata — the write lane's vocabulary is frozen.
 * (Context: the addons table contains both real rows like 'second-shooter'
 * at $500 and never-sold phantom rows like 'second_photographer' at $100;
 * remapping writes would change billing.)
 */

const FAMILY_CODES = {
  second_photographer: ['second-shooter', 'second_shooter', 'second-photographer', 'second_photographer'],
  second_videographer: ['second-videographer', 'second_videographer'],
  video_addon: ['video_addon', 'video-addon'],
  raw_footage: ['raw-footage', 'raw_footage', 'raw_files'],
  engagement_session: ['engagement-session', 'engagement_session'],
  extra_hour: ['extra_hour', 'extra-hour'],
  rush_delivery: ['rush-delivery', 'rush_delivery'],
  liability_insurance: ['liability-insurance', 'liability_insurance'],
  travel_fee: ['travel-fee', 'travel_fee'],
  getting_ready_photos: ['getting-ready-photos', 'getting_ready_photos'],
  hour_on_us: ['hour-on-us', 'hour_on_us']
}

const CODE_TO_FAMILY = {}
for (const [family, codes] of Object.entries(FAMILY_CODES)) {
  for (const code of codes) CODE_TO_FAMILY[code] = family
}

function extractCode(source) {
  if (!source) return ''
  const raw = typeof source === 'string'
    ? source
    : (source.code || source.id || source.addonCode || source.addon_id || '')
  return String(raw).trim().toLowerCase()
}

/** Family key for any code spelling / addon-ish object, or null if unknown. */
export function addonFamily(source) {
  return CODE_TO_FAMILY[extractCode(source)] || null
}

/** True when both sides resolve to the same known family. */
export function sameAddonFamily(a, b) {
  const fa = addonFamily(a)
  return fa !== null && fa === addonFamily(b)
}

export function isSecondPhotographerAddon(source) {
  return addonFamily(source) === 'second_photographer'
}

export function isSecondVideographerAddon(source) {
  return addonFamily(source) === 'second_videographer'
}

export function isVideoAddon(source) {
  return addonFamily(source) === 'video_addon'
}

/**
 * Expand a list of codes to every known spelling in their families, so a DB
 * `.in('code', ...)` fetch can never miss a row over hyphen/underscore drift.
 * Unknown codes pass through unchanged.
 */
export function expandAddonCodes(codes) {
  const out = new Set()
  for (const c of codes || []) {
    const code = extractCode(c)
    if (!code) continue
    const family = CODE_TO_FAMILY[code]
    if (family) FAMILY_CODES[family].forEach(v => out.add(v))
    else out.add(code)
  }
  return [...out]
}

/**
 * Pick the addon row for a request: exact normalized code first, then exact
 * raw code, then family fallback. Exact-first preserves the historical row
 * (and price) for every path that already worked; family fallback rescues
 * paths that previously resolved nothing.
 */
export function pickAddonRow(rows, requested, normalizedCode) {
  if (!rows || rows.length === 0) return undefined
  const raw = extractCode(requested)
  const norm = String(normalizedCode || '').trim().toLowerCase()
  return (
    (norm && rows.find(r => r.code === norm)) ||
    (raw && rows.find(r => r.code === raw)) ||
    (raw && rows.find(r => sameAddonFamily(r.code, raw))) ||
    (norm && rows.find(r => sameAddonFamily(r.code, norm))) ||
    undefined
  )
}
